import { useState, useCallback } from 'react';
import { apiClient, ApiRequestError } from '../api/client';

export type BulkInviteStatus = 'invited' | 'already_member' | 'already_pending' | 'error';

export interface BulkInviteResponse {
  invited: number;
  results: { email: string; status: BulkInviteStatus }[];
}

/** Dados que o backend devolve (HTTP 409) quando a operação passa do limite de usuários do plano. */
export interface PlanLimitInfo {
  code: 'plan_limit_exceeded' | 'plan_limit_ack_required';
  message: string;
  included_users: number;
  current_members: number;
  adding?: number;
}

export function getPlanLimitInfo(err: unknown): PlanLimitInfo | null {
  if (!(err instanceof ApiRequestError) || err.status !== 409) return null;
  const detail = (err.detail as { detail?: unknown } | undefined)?.detail;
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const code = (detail as PlanLimitInfo).code;
    if (code === 'plan_limit_exceeded' || code === 'plan_limit_ack_required') {
      return detail as PlanLimitInfo;
    }
  }
  return null;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  active: boolean;
  is_pending: boolean;
  created_at: string;
  organization_id: string;
}

export function useMembers() {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMembers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiClient.get<Member[]>('/members');
      setMembers(data);
      setError(null);
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const inviteMembersBulk = async (emails: string[], role: string, confirmExtraCharges = false) => {
    const response = await apiClient.post<BulkInviteResponse>('/members/invite-bulk', {
      emails,
      role,
      confirm_extra_charges: confirmExtraCharges,
    });
    await fetchMembers();
    return response;
  };

  const updateMemberRole = async (id: string, role: string) => {
    await apiClient.patch(`/members/${id}`, { role });
    await fetchMembers();
  };

  // Nome do membro só nesta organização; vazio volta ao nome do cadastro
  const updateMemberName = async (id: string, name: string) => {
    await apiClient.patch(`/members/${id}/name`, { name });
    await fetchMembers();
  };

  const removeMember = async (id: string) => {
    await apiClient.delete(`/members/${id}`);
    await fetchMembers();
  };

  return {
    members,
    isLoading,
    error,
    fetchMembers,
    inviteMembersBulk,
    updateMemberRole,
    updateMemberName,
    removeMember,
  };
}
